import React, { createContext, useState, useContext, ReactNode } from 'react';
import { en } from './en';
import { fr } from './fr';

// Define the shape of our translations
export type TranslationType = {
  header: {
    home: string;
    about: string;
    services: string;
    contact: string;
    language: string;
    selectLanguage: string;
    scheduleConsultation: string;
    allServices: string;
    monthlyBookkeeping: string;
    taxPreparation: string;
    financialPlanning: string;
    businessConsulting: string;
  };
  hero: {
    tagline: string;
    title: {
      part1: string;
      highlight1: string;
      part2: string;
      highlight2: string;
    };
    description: string;
    consultation: string;
    contactUs: string;
  };
  about: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
    };
    story: {
      title: string;
      description: string;
      chips: string;
      button: string;
    };
    mission: {
      title: string;
      description: string;
      chips: string;
      button: string;
    };
    values: {
      title: string;
      subtitle: string;
      chips: string;
      value1: {
        title: string;
        description: string;
      };
      value2: {
        title: string;
        description: string;
      };
      value3: {
        title: string;
        description: string;
      };
      value4: {
        title: string;
        description: string;
      };
    };
  };
  contact: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
    };
    info: {
      title: string;
      subtitle: string;
      chips: string;
      phone: {
        label: string;
        value: string;
      };
      email: {
        label: string;
        value: string;
      };
      location: {
        label: string;
        value: string;
      };
      hours: {
        label: string;
        weekdays: string;
        saturday: string;
        sunday: string;
      };
    };
    form: {
      title: string;
    };
  };
  services: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
      button: string;
    };
    monthlyBookkeeping: {
      title: string;
      description: string;
      chips: string;
      button: string;
      benefits: {
        title: string;
        points: string[];
      };
    };
    taxPreparation: {
      title: string;
      description: string;
      chips: string;
      button: string;
      benefits: {
        title: string;
        points: string[];
      };
    };
    financialPlanning: {
      title: string;
      description: string;
      chips: string;
      button: string;
      benefits: {
        title: string;
        points: string[];
      };
    };
    businessConsulting: {
      title: string;
      description: string;
      chips: string;
      button: string;
      benefits: {
        title: string;
        points: string[];
      };
    };
  };
  monthlyBookkeeping: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
      button: string;
    };
    included: {
      title: string;
      subtitle: string;
      chips: string;
      sections: {
        accountsPayable: {
          title: string;
          description: string;
        };
        reconciliations: {
          title: string;
          description: string;
        };
        statements: {
          title: string;
          description: string;
        };
        categorization: {
          title: string;
          description: string;
        };
      };
    };
    benefits: {
      title: string;
      chips: string;
      sections: {
        time: {
          title: string;
          description: string;
        };
        compliance: {
          title: string;
          description: string;
        };
        visibility: {
          title: string;
          description: string;
        };
      };
      button: string;
    };
    faq: {
      title: string;
      chips: string;
      questions: {
        data: {
          question: string;
          answer: string;
        };
        system: {
          question: string;
          answer: string;
        };
        commitment: {
          question: string;
          answer: string;
        };
      };
    };
  };
  taxPreparation: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
      button: string;
    };
    included: {
      title: string;
      subtitle: string;
      chips: string;
      sections: {
        returns: {
          title: string;
          description: string;
        };
        optimization: {
          title: string;
          description: string;
        };
        support: {
          title: string;
          description: string;
        };
        documentation: {
          title: string;
          description: string;
        };
        financialStatements : {
          title: string;
          description: string;
        };
      };
    };
    benefits: {
      title: string;
      chips: string;
      sections: {
        refunds: {
          title: string;
          description: string;
        };
        mistakes: {
          title: string;
          description: string;
        };
        peace: {
          title: string;
          description: string;
        };
      };
      button: string;
    };
    faq: {
      title: string;
      chips: string;
      questions: {
        behind: {
          question: string;
          answer: string;
        };
        selfEmployed: {
          question: string;
          answer: string;
        };
        documents: {
          question: string;
          answer: string;
        };
      };
    };
  };
  financialPlanning: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
      button: string;
    };
    included: {
      title: string;
      subtitle: string;
      chips: string;
      sections: {
        assessment: {
          title: string;
          description: string;
        };
        strategy: {
          title: string;
          description: string;
        };
        cashFlow: {
          title: string;
          description: string;
        };
        growth: {
          title: string;
          description: string;
        };
      };
    };
    benefits: {
      title: string;
      chips: string;
      sections: {
        decisions: {
          title: string;
          description: string;
        };
        growth: {
          title: string;
          description: string;
        };
        profitability: {
          title: string;
          description: string;
        };
      };
      button: string;
    };
    faq: {
      title: string;
      chips: string;
      questions: {
        who: {
          question: string;
          answer: string;
        };
        size: {
          question: string;
          answer: string;
        };
        implementation: {
          question: string;
          answer: string;
        };
      };
    };
  };
  businessConsulting: {
    banner: {
      title: string;
      subtitle: string;
      chips: string;
      button: string;
    };
    included: {
      title: string;
      subtitle: string;
      chips: string;
      sections: {
        startup: {
          title: string;
          description: string;
        };
        financial: {
          title: string;
          description: string;
        };
        cashFlow: {
          title: string;
          description: string;
        };
        growth: {
          title: string;
          description: string;
        };
      };
    };
    benefits: {
      title: string;
      chips: string;
      sections: {
        experience: {
          title: string;
          description: string;
        };
        roadmaps: {
          title: string;
          description: string;
        };
        decisions: {
          title: string;
          description: string;
        };
      };
      button: string;
    };
    faq: {
      title: string;
      chips: string;
      questions: {
        startup: {
          question: string;
          answer: string;
        };
        industries: {
          question: string;
          answer: string;
        };
        implementation: {
          question: string;
          answer: string;
        };
      };
    };
  };
  servicesWeOffer: {
    title: string;
    subtitle: string;
    services: {
      bookkeeping: {
        title: string;
        description: string;
      };
      taxPreparation: {
        title: string;
        description: string;
      };
      financialPlanning: {
        title: string;
        description: string;
      };
      businessConsulting: {
        title: string;
        description: string;
      };
    };
    viewAll: string;
  };
  whatAlgebraCan: {
    benefits: string;
    title: {
      part1: string;
      highlight: string;
      part2: string;
    };
    description: string;
    points: {
      experience: string;
      dedicated: string;
      advanced: string;
    };
  };
  testimonials: {
    title: string;
    subtitle: string;
    testimonials: {
      client1: {
        quote: string;
        name: string;
        position: string;
      };
      client2: {
        quote: string;
        name: string;
        position: string;
      };
      client3: {
        quote: string;
        name: string;
        position: string;
      };
    };
  };
  footer: {
    reliable: {
      title: {
        part1: string;
        highlight1: string;
        part2: string;
        highlight2: string;
      };
      description: string;
      button: string;
    };
    about: string;
    services: {
      title: string;
      taxPlanning: string;
      financialAnalysis: string;
      bookkeepingServices: string;
      businessConsulting: string;
      strategicSupport: String;
    };
    company: {
      title: string;
      services: string;
      aboutUs: string;
      project: string;
      customer: string;
    };
    contact: {
      title: string;
      contactUs: string;
      termsOfService: string;
    };
    copyright: string;
  };
};

// Define the shape of our context
type TranslationContextType = {
  language: string;
  setLanguage: (lang: string) => void;
  t: TranslationType;
};

// Create the context
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Create a provider component
export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState('en');

  // Get the translations based on the selected language
  const getTranslations = (): TranslationType => {
    switch (language) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return en;
    }
  };

  const t = getTranslations();

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

// Create a hook to use the translation context
export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
